import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { Plus, Trash2, Save, LogOut, Database, AlertCircle, MessageCircle, CheckCircle, XCircle, Settings } from 'lucide-react';
import { QuickReply, ResponsePattern, WelcomeMessage } from '../types';
import { supabase } from '../lib/supabase';
import { generateId } from '../utils/generateId';
import ResponsePatternModal from './ResponsePatternModal';
import QuickReplyModal from './QuickReplyModal';
import ApiSettings from './ApiSettings';

const AdminPanel: React.FC = () => {
  const { quickReplies, responsePatterns, welcomeMessages, updateQuickReplies, updateResponsePatterns, updateWelcomeMessages, logout } = useAdmin();
  const navigate = useNavigate();
  
  const [editingReplies, setEditingReplies] = useState<QuickReply[]>(quickReplies);
  const [editingPatterns, setEditingPatterns] = useState<ResponsePattern[]>(responsePatterns);
  const [editingWelcomes, setEditingWelcomes] = useState<WelcomeMessage[]>(welcomeMessages);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'welcome' | 'replies' | 'patterns' | 'api'>('welcome');

  const isSupabaseConfigured = !!supabase;

  const colorOptions = [
    { value: 'blue', label: 'Mavi', preview: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { value: 'red', label: 'Kırmızı', preview: 'bg-gradient-to-r from-red-500 to-red-600' },
    { value: 'purple', label: 'Mor', preview: 'bg-gradient-to-r from-purple-500 to-purple-600' },
    { value: 'green', label: 'Yeşil', preview: 'bg-gradient-to-r from-green-500 to-green-600' },
    { value: 'yellow', label: 'Sarı', preview: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { value: 'pink', label: 'Pembe', preview: 'bg-gradient-to-r from-pink-500 to-pink-600' },
    { value: 'indigo', label: 'İndigo', preview: 'bg-gradient-to-r from-indigo-500 to-indigo-600' },
    { value: 'teal', label: 'Teal', preview: 'bg-gradient-to-r from-teal-500 to-teal-600' },
  ];

  const addQuickReply = (reply: QuickReply) => {
    setEditingReplies([...editingReplies, reply]);
    setShowReplyModal(false);
  };

  const addResponsePattern = (pattern: ResponsePattern) => {
    setEditingPatterns([...editingPatterns, pattern]);
    setShowPatternModal(false);
  };

  const removeQuickReply = (id: string) => {
    setEditingReplies(editingReplies.filter(reply => reply.id !== id));
  };

  const removeResponsePattern = (id: string) => {
    setEditingPatterns(editingPatterns.filter(pattern => pattern.id !== id));
  };

  const addWelcomeMessage = () => {
    const newMessage: WelcomeMessage = {
      id: generateId(),
      message: 'Yeni karşılama mesajı...',
      is_active: false
    };
    setEditingWelcomes([...editingWelcomes, newMessage]);
  };

  const removeWelcomeMessage = (id: string) => {
    setEditingWelcomes(editingWelcomes.filter(message => message.id !== id));
  };

  const setActiveWelcomeMessage = (id: string) => {
    setEditingWelcomes(editingWelcomes.map(message => ({
      ...message,
      is_active: message.id === id
    })));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);
      
      // Boş mesajları filtrele
      const validWelcomes = editingWelcomes.filter(message => message.message.trim() !== '');
      
      await Promise.all([
        updateQuickReplies(editingReplies),
        updateResponsePatterns(editingPatterns),
        updateWelcomeMessages(validWelcomes)
      ]);
      
      setSuccess('Değişiklikler başarıyla kaydedildi!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Değişiklikler kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const DatabaseStatus = () => (
    <div className={`flex items-center p-4 rounded-xl border ${
      isSupabaseConfigured 
        ? 'bg-green-50 border-green-200' 
        : 'bg-yellow-50 border-yellow-200'
    }`}>
      <div className={`p-2 rounded-full mr-3 ${
        isSupabaseConfigured ? 'bg-green-100' : 'bg-yellow-100'
      }`}>
        {isSupabaseConfigured ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <AlertCircle className="h-5 w-5 text-yellow-600" />
        )}
      </div>
      <div className="flex-1">
        <h3 className={`font-semibold text-sm ${
          isSupabaseConfigured ? 'text-green-800' : 'text-yellow-800'
        }`}>
          Veritabanı Durumu
        </h3>
        <p className={`text-xs ${
          isSupabaseConfigured ? 'text-green-700' : 'text-yellow-700'
        }`}>
          {isSupabaseConfigured ? 'Supabase bağlantısı aktif' : 'Yerel depolama kullanılıyor'}
        </p>
      </div>
      <div className={`w-3 h-3 rounded-full ${
        isSupabaseConfigured ? 'bg-green-500' : 'bg-yellow-500'
      }`} />
    </div>
  );

  const TabButton = ({ tab, label, icon: Icon }: { tab: string, label: string, icon: any }) => (
    <button
      onClick={() => setActiveTab(tab as any)}
      className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
        activeTab === tab
          ? 'bg-red-600 text-white shadow-md'
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
      }`}
    >
      <Icon className="h-4 w-4 mr-2" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-1">Admin Paneli</h1>
              <p className="text-gray-600 text-sm">Gürsu AI Asistan yönetim merkezi</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center justify-center text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Çıkış Yap
            </button>
          </div>
        </div>

        {/* Database Status */}
        <div className="mb-6">
          <DatabaseStatus />
        </div>

        {/* Notifications */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 flex items-center">
            <XCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 flex items-center">
            <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 p-4">
            <div className="flex flex-wrap gap-2">
              <TabButton tab="welcome" label="Karşılama" icon={MessageCircle} />
              <TabButton tab="replies" label="Hızlı Yanıtlar" icon={Plus} />
              <TabButton tab="patterns" label="Yanıt Kalıpları" icon={Settings} />
              <TabButton tab="api" label="API Ayarları" icon={Database} />
            </div>
          </div>

          <div className="p-6">
            {/* Welcome Messages Tab */}
            {activeTab === 'welcome' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">Karşılama Mesajları</h2>
                  <button
                    onClick={addWelcomeMessage}
                    className="flex items-center justify-center text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Ekle
                  </button>
                </div>
                
                <div className="space-y-4">
                  {editingWelcomes.map((welcome, index) => (
                    <div key={welcome.id} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex flex-col gap-4">
                        <textarea
                          value={welcome.message}
                          onChange={(e) => {
                            const newWelcomes = [...editingWelcomes];
                            newWelcomes[index].message = e.target.value;
                            setEditingWelcomes(newWelcomes);
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                          rows={2}
                          placeholder="Karşılama mesajı..."
                        />
                        <div className="flex items-center justify-between">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="activeWelcome"
                              checked={welcome.is_active}
                              onChange={() => setActiveWelcomeMessage(welcome.id)}
                              className="mr-2 text-red-600 focus:ring-red-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {welcome.is_active ? 'Aktif Mesaj' : 'Pasif'}
                            </span>
                          </label>
                          <button
                            onClick={() => removeWelcomeMessage(welcome.id)}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Replies Tab */}
            {activeTab === 'replies' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">Hızlı Yanıtlar</h2>
                  <button
                    onClick={() => setShowReplyModal(true)}
                    className="flex items-center justify-center text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Ekle
                  </button>
                </div>
                
                <div className="space-y-4">
                  {editingReplies.map((reply, index) => (
                    <div key={reply.id} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            value={reply.text}
                            onChange={(e) => {
                              const newReplies = [...editingReplies];
                              newReplies[index].text = e.target.value;
                              setEditingReplies(newReplies);
                            }}
                            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                            placeholder="Hızlı yanıt metni"
                          />
                          <button
                            onClick={() => removeQuickReply(reply.id)}
                            className="text-red-500 hover:text-red-700 p-3 hover:bg-red-50 rounded-lg transition-colors sm:self-start"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <textarea
                          value={reply.response || reply.text}
                          onChange={(e) => {
                            const newReplies = [...editingReplies];
                            newReplies[index].response = e.target.value;
                            setEditingReplies(newReplies);
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                          rows={3}
                          placeholder="Bu hızlı yanıt için verilecek cevap..."
                        />
                        
                        <div className="grid grid-cols-4 gap-2">
                          {colorOptions.map((color) => (
                            <button
                              key={color.value}
                              onClick={() => {
                                const newReplies = [...editingReplies];
                                newReplies[index].color = color.value;
                                setEditingReplies(newReplies);
                              }}
                              className={`p-2 rounded-lg border-2 transition-all ${
                                reply.color === color.value 
                                  ? 'border-gray-800 scale-105' 
                                  : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              <div className={`${color.preview} h-6 w-full rounded text-white text-xs flex items-center justify-center font-medium`}>
                                {color.label}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Response Patterns Tab */}
            {activeTab === 'patterns' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">Yanıt Kalıpları</h2>
                  <button
                    onClick={() => setShowPatternModal(true)}
                    className="flex items-center justify-center text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Ekle
                  </button>
                </div>

                <div className="space-y-4">
                  {editingPatterns.map((pattern, index) => (
                    <div key={pattern.id} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            value={pattern.trigger}
                            onChange={(e) => {
                              const newPatterns = [...editingPatterns];
                              newPatterns[index].trigger = e.target.value;
                              setEditingPatterns(newPatterns);
                            }}
                            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                            placeholder="Tetikleyici kelimeler (virgül ile ayırın)"
                          />
                          <button
                            onClick={() => removeResponsePattern(pattern.id)}
                            className="text-red-500 hover:text-red-700 p-3 hover:bg-red-50 rounded-lg transition-colors sm:self-start"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <textarea
                          value={pattern.response}
                          onChange={(e) => {
                            const newPatterns = [...editingPatterns];
                            newPatterns[index].response = e.target.value;
                            setEditingPatterns(newPatterns);
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                          rows={3}
                          placeholder="Yanıt metni"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* API Settings Tab */}
            {activeTab === 'api' && (
              <ApiSettings />
            )}
          </div>
        </div>

        {/* Save Button - Hidden on API tab */}
        {activeTab !== 'api' && (
          <div className="mt-6">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white py-4 rounded-xl hover:from-red-700 hover:to-red-600 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] font-medium"
            >
              <Save className="h-5 w-5 mr-2" />
              {isSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPatternModal && (
        <ResponsePatternModal
          onClose={() => setShowPatternModal(false)}
          onSave={addResponsePattern}
        />
      )}

      {showReplyModal && (
        <QuickReplyModal
          onClose={() => setShowReplyModal(false)}
          onSave={addQuickReply}
        />
      )}
    </div>
  );
};

export default AdminPanel;